import { getPendingPixList, releasePixActive, updatePendingPix } from "modules/payment/application/pix/pendingPixQueue";
import { ISyncOrderResult } from "../application/useCases/SyncOrderUseCase";

export type PixLogger = (log: string, data: any) => void;

// O log nunca pode impedir a atualização da fila
const safeLog = (logger: PixLogger | undefined, data: { message: string; orderId?: string; pixId: string }) => {
	try {
		logger?.("pix", data);
	} catch {
		// ignora
	}
};

/**
 * Atualiza o item da fila de PIX depois do envio feito pela tela e libera o dono ativo.
 * Retorna true quando a NFC-e fica para o verificador (envio falhou): a tela não deve emitir.
 */
export const applyScreenPixSyncResult = (
	pixId: string,
	result: ISyncOrderResult | undefined,
	logger?: PixLogger
): boolean => {
	const success = !!result?.success;
	try {
		const current = getPendingPixList().find((it) => it.pixId === pixId);
		const attempts = (current?.sendAttempts ?? 0) + 1;
		const failure = result?.message ?? "Falha ao sincronizar pagamento";
		if (success) {
			updatePendingPix(pixId, { status: "sent", sendAttempts: attempts });
		} else {
			updatePendingPix(pixId, {
				status: "send_failed",
				sendAttempts: attempts,
				lastError: failure,
				nfcePending: true,
			});
		}
		safeLog(logger, {
			message: success ? `PIX ${pixId} enviado pela tela` : `PIX ${pixId} falha no envio pela tela: ${failure}`,
			orderId: current?.order?.id,
			pixId,
		});
	} finally {
		releasePixActive(pixId);
	}
	return !success;
};

/**
 * Roda o envio da tela e, para PIX enfileirável (pixId definido), sempre aplica o resultado na fila,
 * mesmo se algo lançar antes do envio (o erro é repassado).
 */
export const runScreenPixSync = async (
	pixId: string | undefined,
	sync: () => Promise<ISyncOrderResult | undefined>,
	logger?: PixLogger
): Promise<{ syncResult: ISyncOrderResult | undefined; nfceDeferred: boolean }> => {
	if (!pixId) {
		return { syncResult: await sync(), nfceDeferred: false };
	}
	let syncResult: ISyncOrderResult | undefined;
	let nfceDeferred = true;
	try {
		syncResult = await sync();
	} finally {
		nfceDeferred = applyScreenPixSyncResult(pixId, syncResult, logger);
	}
	return { syncResult, nfceDeferred };
};
